import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Brain,
  FileJson,
  FileText,
  Info,
  Lightbulb,
  Pencil,
} from 'lucide-react';

import { Button } from 'components/ui/button';
import { Textarea } from 'components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import type { IGetAgentModelResponse } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useIsMobile } from 'hooks/use-mobile';
import { useToast } from 'hooks/use-toast';
import { cn } from 'lib/utils';
import { AgentModelPicker, type ModelSelectionValue } from './agent-model-picker';
import { BasePromptAiModal } from './base-prompt-ai-modal';
import { JsonSchemaEditorModal } from './json-schema-editor-modal';

type LlmConfigurationSectionProps = {
  isLoadingLlmModels: boolean;
  availableChatModels: IGetAgentModelResponse;
  availableCustomChatModels: IGetAgentModelResponse;
  currentLlmModel: string;
  currentLlmProvider: string;
  currentLlmModelId: string;
  isBlocksProvider: boolean;
  onModelChange: (value: ModelSelectionValue) => void;
  responseType: string;
  onResponseTypeChange: (value: string) => void;
  responseFormat: string | null;
  onResponseFormatChange: (value: string | null) => void;
  basePrompt: string;
  onBasePromptChange: (value: string) => void;
  autoBasePrompt: string;
  temperatureValue: number;
  onTemperatureChange: (value: number) => void;
  maxResponseValue: number;
  onMaxResponseChange: (value: number) => void;
  isModelParametersOpen: boolean;
  onModelParametersOpenChange: (open: boolean) => void;
  agentName?: string;
  selectedToolIds?: string[];
  disabled?: boolean;
};

export function LlmConfigurationSection({
  isLoadingLlmModels,
  availableChatModels,
  availableCustomChatModels,
  currentLlmModel,
  currentLlmProvider,
  currentLlmModelId,
  isBlocksProvider,
  onModelChange,
  responseType,
  onResponseTypeChange,
  responseFormat,
  onResponseFormatChange,
  basePrompt,
  onBasePromptChange,
  autoBasePrompt,
  temperatureValue,
  onTemperatureChange,
  maxResponseValue,
  onMaxResponseChange,
  isModelParametersOpen,
  onModelParametersOpenChange,
  agentName,
  selectedToolIds,
  disabled = false,
}: LlmConfigurationSectionProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isJsonEditorOpen, setIsJsonEditorOpen] = useState(false);
  const [isPromptEmptyWarning, setIsPromptEmptyWarning] = useState(false);
  const [isBasePromptAiModalOpen, setIsBasePromptAiModalOpen] = useState(false);
  const modelParametersAnchorRef = useRef<HTMLDivElement | null>(null);
  const [modelParametersPopoverWidth, setModelParametersPopoverWidth] = useState<number>();
  const isMobile = useIsMobile();
  const temperature = temperatureValue.toFixed(2);
  const maxTokens = maxResponseValue;
  const exampleSchema = useMemo(
    () =>
      JSON.stringify(
        {
          answer: 'string',
          question: 'string',
        },
        null,
        2
      ),
    []
  );
  const visibleResponseFormat = responseFormat || exampleSchema;
  const isExampleSchema = visibleResponseFormat === exampleSchema;
  const basePromptTokenCount = useMemo(() => {
    if (!basePrompt.trim()) return 0;

    return basePrompt
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
  }, [basePrompt]);

  useEffect(() => {
    if (basePrompt.trim()) setIsPromptEmptyWarning(false);
  }, [basePrompt]);

  const handleBasePromptBlur = () => {
    if (basePrompt.trim()) return;

    setIsPromptEmptyWarning(true);
    onBasePromptChange(autoBasePrompt);
    toast({
      variant: 'warning',
      title: t('BASE_PROMPT_CANNOT_BE_EMPTY') || "Base prompt can't be empty",
      description:
        t('DEFAULT_PROMPT_APPLIED_INSTEAD') || 'A default prompt has been applied instead.',
    });
  };

  useEffect(() => {
    if (!isModelParametersOpen) return undefined;

    const updatePopoverWidth = () => {
      if (modelParametersAnchorRef.current) {
        setModelParametersPopoverWidth(modelParametersAnchorRef.current.offsetWidth);
      }
    };

    updatePopoverWidth();
    window.addEventListener('resize', updatePopoverWidth);
    return () => window.removeEventListener('resize', updatePopoverWidth);
  }, [isModelParametersOpen, isMobile]);

  const renderModelParameterControl = (
    label: string,
    value: number,
    max: number,
    min: number,
    step: number,
    onChange: (nextValue: number) => void
  ) => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <Info className="h-4 w-4 shrink-0 text-muted-foreground" />
      </div>
      <div
        className={cn(
          'flex gap-3',
          isMobile ? 'flex-col items-stretch' : 'flex-row items-center'
        )}
      >
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(Number(event.target.value))}
          className="h-2 w-full min-w-0 flex-1 cursor-pointer appearance-none rounded-full bg-muted accent-primary disabled:cursor-not-allowed disabled:opacity-50"
        />
        <div
          className={cn(
            'shrink-0 rounded-lg border border-border bg-background px-3 py-2 text-right',
            isMobile ? 'w-full' : 'min-w-[130px]'
          )}
        >
          <span
            className={cn(
              'font-semibold text-foreground',
              isMobile ? 'text-xl' : 'text-2xl'
            )}
          >
            {step < 1 ? value.toFixed(2) : value}
          </span>
          <span className="ml-1 text-sm text-muted-foreground">
            / {step < 1 ? max.toFixed(2) : max}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <fieldset
      disabled={disabled}
      className="m-0 min-w-0 space-y-6 border-0 p-0 disabled:opacity-70"
    >
      <div className={cn('space-y-2', isMobile ? 'w-full max-w-full' : 'max-w-sm')}>
        <p className="text-sm font-medium text-foreground">{t('SELECT_MODEL') || 'Select Model'}</p>
        <AgentModelPicker
          isLoadingLlmModels={isLoadingLlmModels}
          availableChatModels={availableChatModels}
          availableCustomChatModels={availableCustomChatModels}
          currentLlmModel={currentLlmModel}
          currentLlmProvider={currentLlmProvider}
          currentLlmModelId={currentLlmModelId}
          isBlocksProvider={isBlocksProvider}
          onModelChange={onModelChange}
          disabled={disabled}
        />
      </div>

      {/* Model Parameters */}

      {/* <div
        ref={modelParametersAnchorRef}
        className={cn('w-full space-y-3', isMobile ? 'max-w-full' : 'max-w-sm')}
      >
        <p className="text-sm font-medium text-foreground">{t('MODEL_PARAMETERS') || 'Model Parameters'}</p>
        <Popover
          open={isModelParametersOpen}
          onOpenChange={onModelParametersOpenChange}
        >
          <PopoverAnchor asChild>
            <div className="flex w-full flex-wrap gap-3">
              <button
                type="button"
                onClick={() => onModelParametersOpenChange(true)}
                className="rounded-lg bg-muted px-4 py-3 text-sm text-foreground transition-colors hover:bg-muted/80"
              >
                <span className="text-muted-foreground">{t('TEMPERATURE_LABEL') || 'Temperature:'}</span>{' '}
                <span className="font-semibold text-primary dark:text-[#ffffff]">{temperature}</span>
              </button>
              <button
                type="button"
                onClick={() => onModelParametersOpenChange(true)}
                className="rounded-lg bg-muted px-4 py-3 text-sm text-foreground transition-colors hover:bg-muted/80"
              >
                <span className="text-muted-foreground">
                  {t('MAXIMUM_RESPONSE_LABEL') || 'Maximum response:'}
                </span>{' '}
                <span className="font-semibold text-primary dark:text-[#ffffff]">{maxTokens}</span>
              </button>
            </div>
          </PopoverAnchor>
          <PopoverContent
            align="start"
            side="bottom"
            sideOffset={12}
            collisionPadding={16}
            style={
              modelParametersPopoverWidth
                ? { width: modelParametersPopoverWidth }
                : undefined
            }
            className="w-auto max-w-[520px] rounded-lg border border-border bg-background p-0"
          >
            <div className={cn('space-y-6', isMobile ? 'p-4' : 'space-y-8 p-6')}>
              <h3
                className={cn(
                  'font-semibold text-foreground',
                  isMobile ? 'text-lg' : 'text-2xl'
                )}
              >
                {t('MODEL_PARAMETERS') || 'Model Parameters'}
              </h3>
              {renderModelParameterControl(
                t('TEMPERATURE') || 'Temperature',
                temperatureValue,
                1,
                0,
                0.01,
                onTemperatureChange
              )}
              {renderModelParameterControl(
                t('MAXIMUM_RESPONSE') || 'Maximum Response',
                maxResponseValue,
                10000,
                2,
                1,
                onMaxResponseChange
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div> */}

      {/* Response Type */}

      {/* <div className="max-w-sm space-y-2">
        <p className="text-sm font-medium text-foreground">{t('RESPONSE_TYPE') || 'Response Type'}</p>
        <Select value={responseType} onValueChange={onResponseTypeChange}>
          <SelectTrigger className="h-11">
            <SelectValue placeholder={t('SELECT_RESPONSE_TYPE') || 'Select response type'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="text">
              <span className="inline-flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t('TEXT') || 'Text'}
              </span>
            </SelectItem>
            <SelectItem value="json">
              <span className="inline-flex items-center gap-2">
                <FileJson className="h-4 w-4" />
                {t('JSON') || 'JSON'}
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </div> */}

      {responseType === 'json' ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-foreground">{t('RESPONSE_SCHEMA') || 'Response Schema'}</p>
              <Info className="h-4 w-4 text-muted-foreground" />
            </div>
            <Button
              type="button"
              variant="outline"
              className="h-11 gap-2 px-4 text-sm"
              onClick={() => setIsJsonEditorOpen(true)}
            >
              <Pencil className="h-4 w-4" />
              {t('EDIT_JSON') || 'Edit JSON'}
            </Button>
          </div>

          <div className="space-y-2">
            {isExampleSchema ? (
              <div className="rounded-lg border-l-4 border-primary bg-blue-50 px-4 py-3 text-primary">
                <div className="flex items-start gap-2">
                  <Lightbulb className="mt-0.5 h-4 w-4 shrink-0" />
                  <p className="text-sm">
                    {t('EXAMPLE_SCHEMA_CLICK_EDIT_JSON_TO_CUSTOMIZE') ||
                      'Example schema • Click Edit JSON to customize'}
                  </p>
                </div>
              </div>
            ) : null}

            <div className="overflow-hidden rounded-lg border border-border bg-background">
              <div className="max-h-[220px] overflow-auto p-5">
                <pre className="whitespace-pre-wrap break-words text-sm leading-7 text-foreground">
                  {visibleResponseFormat}
                </pre>
              </div>
              <div className="flex justify-center border-t border-border bg-muted/20 px-4 py-2">
                <div className="h-1 w-12 rounded-full bg-border" />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-foreground">{t('BASE_PROMPT') || 'Base Prompt'}</p>
            <Info className="h-4 w-4 text-muted-foreground" />
          </div>

          {/* AI Base Prompt */}
          {/* <Button
            type="button"
            variant="secondary"
            className="h-9 rounded-full border border-primary/30 bg-muted px-4 text-sm text-primary dark:text-[#ffffff] hover:bg-muted/80"
            onClick={() => setIsBasePromptAiModalOpen(true)}
          >
            <Brain className="h-4 w-4" />
            <span className="dark:text-[#ffffff]">AI</span>
          </Button> */}
        </div>

        <div
          className={cn(
            'overflow-hidden rounded-lg border border-border bg-background',
            isPromptEmptyWarning && 'border-destructive'
          )}
        >
          <Textarea
            value={basePrompt}
            disabled={disabled}
            onChange={(event) => onBasePromptChange(event.target.value)}
            onBlur={handleBasePromptBlur}
            placeholder={
              disabled
                ? t('ASK_AGENT_OWNER_TO_ADD_BASE_PROMPT') ||
                'Ask the agent owner to add a base prompt'
                : t('ADD_BASE_PROMPT_FOR_THIS_AGENT') || 'Add a base prompt for this agent'
            }
            className="min-h-[260px] resize-y border-0 px-5 py-4 text-sm leading-6 shadow-none focus-visible:ring-0"
          />
          <div className="flex items-center justify-end border-t border-border bg-muted/20 px-4 py-2 text-sm text-muted-foreground">
            {basePromptTokenCount}{' '}
            {basePromptTokenCount === 1 ? t('TOKEN') || 'Token' : t('TOKENS') || 'Tokens'}
          </div>
        </div>
      </div>

      <JsonSchemaEditorModal
        open={disabled ? false : isJsonEditorOpen}
        initialValue={visibleResponseFormat}
        onOpenChange={setIsJsonEditorOpen}
        onSave={onResponseFormatChange}
      />
      <BasePromptAiModal
        open={disabled ? false : isBasePromptAiModalOpen}
        onOpenChange={setIsBasePromptAiModalOpen}
        onUse={onBasePromptChange}
        agentName={agentName}
        basePromptValue={basePrompt}
        toolIds={selectedToolIds}
      />
    </fieldset>
  );
}
